import React, {useEffect, useRef, useState} from "react";
import {Cmhtml} from "../../../../packages/graphql-layer/src";
import CCOContainer from "../../fundamentals/CCOContainer";
import {checkStringProperty, getKey} from "../../../utility/componentUtil";
import {metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from "../../../context/PreviewContext";
import {sanitizeUrlValue} from "../../../utility/sanitizerUtil";

export interface Props {
    cmHtml: Cmhtml;
    isPreview?: boolean;
}

const PLACEHOLDER_REGEX = /\{\{(.*?)\}\}/g;

const replaceUrlParamPlaceholders = (html: string, placeholders: Record<string, string>): string => {
    if (!placeholders || typeof html !== "string") return html;

    return html.replace(PLACEHOLDER_REGEX, (_, key) => {
        if (!(key in placeholders)) {
            return '';
        }

        const localStorageValue = localStorage.getItem(key);
        if (localStorageValue !== null) {
            const sanitizedLocalValue = sanitizeUrlValue(localStorageValue);
            return sanitizedLocalValue ?? '';
        }

        const placeholderValue = placeholders[key];
        const sanitizedPlaceholderValue = sanitizeUrlValue(placeholderValue);
        return sanitizedPlaceholderValue ?? '';
    });
}

function C067HtmlFragment(props: Props) {
    const [isReady, setIsReady] = useState(false);
    const [isIframeSet, setIsIframeSet] = useState(false);
    const isPreview = usePreviewContext().isPreview;
    const [html, setHtml] = useState<string | null>(null);
    const [script, setScript] = useState<string | null>(null);

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const urlParamPlaceholders = props.cmHtml?.htmlSettings?.placeholders;

    const handleResize = () => {
        if (iframeRef.current && iframeRef.current.contentDocument?.body) {
            const newHeight = `${iframeRef.current.contentDocument.body.scrollHeight}px`;
            if (iframeRef.current.style.height !== newHeight) {
                iframeRef.current.style.height = newHeight;
            }
        }
    };

    // Adjust iframe height on window resize
    useEffect(() => {
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (props.cmHtml && props.cmHtml.html) {
            const htmlFragment = checkStringProperty(props.cmHtml!.html!);

            if (!script) {
                // Extract script from html. May be possibly extended if multiple scripts are present in the html fragment.
                const scriptMatch = /<script\b[^>]*type=['"]text\/javascript['"][^>]*>([\s\S]*?)<\/script>/gi.exec(htmlFragment);
                if (scriptMatch) {
                    setScript(scriptMatch[0]);
                    htmlFragment.replace(scriptMatch[0], "");
                }
            }

            if (script) {
                const iframe = iframeRef.current;
                if (iframe) {
                    const onLoadHandler = () => {
                        const iframeDoc = iframe.contentDocument;
                        if (iframeDoc) {
                            const iframeHtml = iframeDoc.querySelector("html");
                            const iframeBody = iframeDoc.querySelector("body");

                            if (iframeHtml) {
                                iframeHtml.style.overflow = "hidden";
                            }

                            if (iframeBody) {
                                iframeBody.style.overflow = "hidden";
                                iframeBody.style.margin = "0";
                            }

                            handleResize(); // Call handleResize initially
                            const observer = new MutationObserver(handleResize);
                            observer.observe(iframe.contentDocument!.body, {
                                attributes: true,
                                childList: true,
                                subtree: true,
                            });
                        }

                        iframe.removeEventListener("load", onLoadHandler);

                    };

                    iframe.addEventListener("load", onLoadHandler);
                    iframe.srcdoc = replaceUrlParamPlaceholders(htmlFragment, urlParamPlaceholders);
                } else {
                    setIsIframeSet(true);
                }

                setHtml(null);
            } else {
                setHtml(replaceUrlParamPlaceholders(htmlFragment, urlParamPlaceholders));
            }
            setIsReady(true);

        }
    }, [props.cmHtml, isIframeSet, script]);

    const show = props?.isPreview || props.cmHtml?.activeElement;

    return show && isReady ? (
        <CCOContainer id={props.cmHtml.id} key={getKey(props.cmHtml.id)}>
            {html !== null ? (
                <div {...metaDataProperty(isPreview, 'properties.cmHtml', props.cmHtml.id)} dangerouslySetInnerHTML={{__html: html}}/>
            ) : (
                <iframe
                    title="HTML Frame"
                    ref={iframeRef}
                    style={{display: "block", width: "100%", border: "none"}}
                />
            )}
        </CCOContainer>
    ) : null;
}

export default C067HtmlFragment;
